import { useState, useEffect } from 'react'
import Map, { Marker, NavigationControl, FullscreenControl, type ViewStateChangeEvent } from 'react-map-gl/mapbox'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Truck } from 'lucide-react'
import 'mapbox-gl/dist/mapbox-gl.css'

interface Vehicle {
  id: string
  name: string
  status: string
  location: {
    latitude: number
    longitude: number
    address: string
  }
  speed: number
  driver: {
    id: string
    name: string
  }
}

interface MapContainerProps {
  onVehicleClick?: (vehicleId: string) => void
}

export function MapContainer({ onVehicleClick }: MapContainerProps) {
  const [vehicles, setVehicles] = useState<Vehicle[]>([])
  const [viewState, setViewState] = useState({
    longitude: -122.4,
    latitude: 37.8,
    zoom: 10
  })

  // Fetch vehicles
  useEffect(() => {
    fetch('/api/fleet/vehicles')
      .then(res => res.json())
      .then(data => setVehicles(data.vehicles))
      .catch(err => console.error('Failed to fetch vehicles:', err))
  }, [])

  const getMarkerColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500'
      case 'idle':
        return 'bg-yellow-500'
      case 'offline':
        return 'bg-gray-500'
      default:
        return 'bg-blue-500'
    }
  }

  return (
    <Card className="h-full w-full overflow-hidden rounded-none border-0">
      <Map
        {...viewState}
        onMove={(evt: ViewStateChangeEvent) => setViewState(evt.viewState)}
        mapStyle="mapbox://styles/mapbox/dark-v11"
        mapboxAccessToken={import.meta.env.VITE_MAPBOX_ACCESS_TOKEN}
        style={{ width: '100%', height: '100%' }}
      >
        <NavigationControl position="top-right" />
        <FullscreenControl position="top-right" />

        {/* Vehicle Markers */}
        {vehicles.map(vehicle => (
          <Marker
            key={vehicle.id}
            longitude={vehicle.location.longitude}
            latitude={vehicle.location.latitude}
            anchor="center"
          >
            <div
              className="relative cursor-pointer group"
              onClick={() => onVehicleClick?.(vehicle.id)}
            >
              <div className={`p-2 rounded-full ${getMarkerColor(vehicle.status)} shadow-lg hover:scale-110 transition-transform`}>
                <Truck className="h-5 w-5 text-white" />
              </div>

              {/* Hover label */}
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                <Badge variant="secondary" className="shadow-md whitespace-nowrap">
                  {vehicle.name} - {vehicle.driver.name}
                </Badge>
              </div>
            </div>
          </Marker>
        ))}
      </Map>
    </Card>
  )
}
